import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Users, ChevronLeft, ChevronRight, Calendar as CalendarIcon, Clock, Car, AlertTriangle,
  CheckCircle2, X, Save, Coffee, Heart, Plane, Loader2, RotateCcw,
} from 'lucide-react';
import { useAuth, API } from '../../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const STATUS_META = {
  working:    { label: 'Working',    color: 'bg-emerald-100 text-emerald-800 border-emerald-300', dot: 'bg-emerald-500', icon: CheckCircle2 },
  day_off:    { label: 'Day Off',    color: 'bg-slate-200 text-slate-800 border-slate-400',       dot: 'bg-slate-500',   icon: Coffee },
  sick_leave: { label: 'Sick Leave', color: 'bg-amber-100 text-amber-800 border-amber-300',       dot: 'bg-amber-500',   icon: Heart },
  holiday:    { label: 'Holiday',    color: 'bg-sky-100 text-sky-800 border-sky-300',             dot: 'bg-sky-500',     icon: Plane },
  unknown:    { label: '—',          color: 'bg-white text-slate-400 border-slate-200',            dot: 'bg-slate-300',   icon: null },
};

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

function formatHours(h) {
  if (!h) return '—';
  const hours = Math.floor(h);
  const mins = Math.round((h - hours) * 60);
  return mins ? `${hours}h ${mins}m` : `${hours}h`;
}

function ymdToday() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export default function DriverAttendance() {
  const { token } = useAuth();
  const today = ymdToday();
  const [drivers, setDrivers] = useState([]);
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1); // 1..12
  const [selectedDriverId, setSelectedDriverId] = useState('');
  const [calendar, setCalendar] = useState(null);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('driver');

  // Day editor modal
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null); // { driver_id, date, status, shift_start, shift_end, notes, rides_count }
  const [saving, setSaving] = useState(false);
  // Confirm "has rides" warning
  const [warnRidesOpen, setWarnRidesOpen] = useState(false);
  const [pendingChange, setPendingChange] = useState(null);

  const fetchDrivers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } });
      const list = (res.data || []).filter((u) => ['driver', 'admin-driver'].includes(u.role) && u.active !== false);
      list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setDrivers(list);
    } catch (err) {
      toast.error('Failed to load drivers');
    }
  }, [token]);

  const fetchCalendar = useCallback(async () => {
    if (!selectedDriverId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/driver-attendance/calendar`, {
        params: { driver_id: selectedDriverId, year, month },
        headers: { Authorization: `Bearer ${token}` },
      });
      setCalendar(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load calendar');
    } finally {
      setLoading(false);
    }
  }, [token, selectedDriverId, year, month]);

  const fetchOverview = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/driver-attendance/overview`, {
        params: { year, month },
        headers: { Authorization: `Bearer ${token}` },
      });
      setOverview(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load overview');
    } finally {
      setLoading(false);
    }
  }, [token, year, month]);

  useEffect(() => { fetchDrivers(); }, [fetchDrivers]);
  useEffect(() => {
    if (!selectedDriverId && drivers.length > 0) {
      setSelectedDriverId(drivers[0].id);
    }
  }, [drivers, selectedDriverId]);
  useEffect(() => { if (tab === 'driver') fetchCalendar(); }, [tab, fetchCalendar]);
  useEffect(() => { if (tab === 'all') fetchOverview(); }, [tab, fetchOverview]);

  const navMonth = (delta) => {
    let m = month + delta;
    let y = year;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    setMonth(m); setYear(y);
  };

  const openDayEditor = (day, driverId) => {
    setEditing({
      driver_id: driverId,
      date: day.date,
      // Treat "unknown" (no record yet) as "working" in the editor so saving
      // creates a working-day record instead of throwing on the unknown status.
      status: (day.status && day.status !== 'unknown') ? day.status : 'working',
      shift_start: day.shift_start || '',
      shift_end: day.shift_end || '',
      notes: day.notes || '',
      rides_count: day.rides_count || 0,
      worked_hours: day.worked_hours || 0,
      first_pickup: day.first_pickup,
      last_dropoff: day.last_dropoff,
      shift_open: !!day.shift_open,
    });
    setEditorOpen(true);
  };

  const reopenShift = async () => {
    if (!editing) return;
    if (!window.confirm(`Reopen the shift for ${editing.date}? The driver will be able to mark rides again.`)) return;
    setSaving(true);
    try {
      await axios.post(
        `${API}/driver-attendance/reopen-shift`,
        null,
        {
          params: { driver_id: editing.driver_id, date: editing.date },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.success('Shift reopened — driver can mark rides again.');
      setEditorOpen(false);
      setEditing(null);
      if (tab === 'driver') fetchCalendar(); else fetchOverview();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reopen shift');
    } finally {
      setSaving(false);
    }
  };

  const performSave = async (override) => {
    const body = override || editing;
    setSaving(true);
    try {
      await axios.post(`${API}/driver-attendance`, {
        driver_id: body.driver_id,
        date: body.date,
        status: body.status,
        shift_start: body.shift_start || null,
        shift_end: body.shift_end || null,
        notes: body.notes || null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Attendance saved');
      setEditorOpen(false);
      setWarnRidesOpen(false);
      setEditing(null);
      setPendingChange(null);
      if (tab === 'driver') fetchCalendar(); else fetchOverview();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save attendance');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveClick = () => {
    if (!editing) return;
    // If switching to off-duty status (day_off/sick/holiday) while the driver has rides, warn first
    const blocked = ['day_off', 'sick_leave', 'holiday'];
    if (blocked.includes(editing.status) && editing.rides_count > 0) {
      setPendingChange(editing);
      setWarnRidesOpen(true);
      return;
    }
    performSave();
  };

  // Calendar grid helpers
  const monthDays = calendar?.days || [];
  const firstDow = useMemo(() => {
    if (!monthDays.length) return 0;
    const [y, m, d] = monthDays[0].date.split('-').map(Number);
    return new Date(y, m - 1, d).getDay(); // 0=Sun
  }, [monthDays]);
  // We want Monday as the first column → shift
  const firstColOffset = (firstDow + 6) % 7;

  return (
    <div className="space-y-6" data-testid="driver-attendance-page">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Users className="h-6 w-6 text-indigo-600" />
            Driver Attendance
          </h1>
          <p className="text-sm text-slate-500 mt-1">Track working days, day offs, sick leaves, holidays and worked hours.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => navMonth(-1)} data-testid="attendance-prev-month"><ChevronLeft className="h-4 w-4" /></Button>
          <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
            <SelectTrigger className="w-36" data-testid="attendance-month-select"><SelectValue /></SelectTrigger>
            <SelectContent>{MONTHS.map((m, i) => <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="w-24" data-testid="attendance-year-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[2024, 2025, 2026, 2027, 2028].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={() => navMonth(1)} data-testid="attendance-next-month"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-2 text-xs">
        {Object.entries(STATUS_META).filter(([k]) => k !== 'unknown').map(([k, m]) => (
          <span key={k} className={`px-2 py-1 rounded border ${m.color} flex items-center gap-1`}>
            <span className={`h-2 w-2 rounded-full ${m.dot}`} />
            {m.label}
          </span>
        ))}
      </div>

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="driver" data-testid="attendance-tab-driver">Single Driver</TabsTrigger>
          <TabsTrigger value="all" data-testid="attendance-tab-all">All Drivers</TabsTrigger>
        </TabsList>

        {/* ===== SINGLE DRIVER TAB ===== */}
        <TabsContent value="driver" className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <Select value={selectedDriverId} onValueChange={setSelectedDriverId}>
              <SelectTrigger className="w-full sm:w-72" data-testid="attendance-driver-select">
                <SelectValue placeholder="Select driver" />
              </SelectTrigger>
              <SelectContent>
                {drivers.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}{d.role === 'admin-driver' ? ' (Admin)' : ''}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Monthly totals cards */}
          {calendar && (
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="attendance-totals">
              <SummaryCard label="Working days"  value={calendar.totals.working}     icon={CheckCircle2} accent="emerald" />
              <SummaryCard label="Day Offs"      value={calendar.totals.day_off}     icon={Coffee}       accent="slate" />
              <SummaryCard label="Sick days"     value={calendar.totals.sick_leave}  icon={Heart}        accent="amber" />
              <SummaryCard label="Holidays"      value={calendar.totals.holiday}     icon={Plane}        accent="sky" />
              <SummaryCard label="Worked hours"  value={formatHours(calendar.totals.worked_hours)} icon={Clock} accent="indigo" />
              <SummaryCard label="Missing shifts" value={calendar.totals.missing_shift || 0} icon={AlertTriangle} accent="rose" />
            </div>
          )}

          {/* Calendar grid */}
          <div className="bg-white border border-slate-200 rounded-md shadow-sm p-3">
            <div className="grid grid-cols-7 gap-1 text-[11px] font-medium text-slate-500 mb-1 px-1">
              {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map((d) => <div key={d} className="text-center">{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {Array.from({ length: firstColOffset }).map((_, i) => <div key={`pad-${i}`} className="h-24 bg-slate-50/50 rounded" />)}
              {monthDays.map((day) => {
                const meta = STATUS_META[day.status] || STATUS_META.working;
                const Icon = meta.icon;
                return (
                  <button
                    key={day.date}
                    type="button"
                    onClick={() => openDayEditor(day, selectedDriverId)}
                    className={`h-24 p-1.5 rounded text-left border ${meta.color} hover:shadow-md transition relative ${day.shift_missing ? 'ring-2 ring-rose-300 ring-offset-1' : ''}`}
                    data-testid={`attendance-day-${day.date}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold">{day.date.split('-')[2]}</span>
                      <div className="flex items-center gap-0.5">
                        {day.shift_missing && (
                          <AlertTriangle className="h-3 w-3 text-rose-600" title="Shift start/end not registered" />
                        )}
                        {Icon && <Icon className="h-3 w-3 opacity-70" />}
                      </div>
                    </div>
                    <div className="text-[10px] mt-1 space-y-0.5">
                      {day.rides_count > 0 && (
                        <div className="flex items-center gap-1">
                          <Car className="h-2.5 w-2.5" />
                          <span>{day.rides_count} ride{day.rides_count !== 1 ? 's' : ''}</span>
                        </div>
                      )}
                      {day.worked_hours > 0 && (
                        <div className="flex items-center gap-1 font-medium">
                          <Clock className="h-2.5 w-2.5" />
                          <span>{formatHours(day.worked_hours)}</span>
                        </div>
                      )}
                      {(day.shift_start || day.shift_end) && (
                        <div className="text-[9px] italic opacity-80">
                          {day.shift_start || '—'} → {day.shift_end || '—'}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
            {loading && <div className="text-center text-sm text-slate-500 py-4 flex items-center justify-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>}
          </div>
        </TabsContent>

        {/* ===== ALL DRIVERS TAB ===== */}
        <TabsContent value="all" className="space-y-3">
          {overview && (
            <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-x-auto" data-testid="attendance-overview-grid">
              <table className="min-w-full text-xs">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold text-slate-700 sticky left-0 bg-slate-50 z-10 min-w-[160px]">Driver</th>
                    {overview.days.map((d) => (
                      <th key={d} className="px-1 py-2 text-center font-medium text-slate-600 min-w-[28px]">{d.split('-')[2]}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {overview.drivers.map((drv) => (
                    <tr key={drv.driver_id} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="px-3 py-1.5 sticky left-0 bg-white font-medium text-slate-800 z-10 whitespace-nowrap">
                        {drv.driver_name}
                        {drv.driver_role === 'admin-driver' && <span className="ml-1 text-[9px] text-indigo-600">(A)</span>}
                      </td>
                      {drv.days.map((d) => {
                        const meta = STATUS_META[d.status] || STATUS_META.working;
                        return (
                          <td key={d.date} className="px-0.5 py-0.5 text-center">
                            <button
                              type="button"
                              onClick={() => openDayEditor({ ...d, rides_count: 0 }, drv.driver_id)}
                              className={`w-6 h-6 rounded-sm ${meta.dot} hover:ring-2 hover:ring-offset-1 hover:ring-indigo-400 transition`}
                              title={`${meta.label} — ${d.date}`}
                              data-testid={`attendance-grid-${drv.driver_id}-${d.date}`}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Day editor modal */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-md" data-testid="attendance-editor-dialog">
          <DialogHeader>
            <DialogTitle>Edit attendance — {editing?.date}</DialogTitle>
            <DialogDescription>
              {drivers.find((d) => d.id === editing?.driver_id)?.name || 'Driver'}
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              {editing.rides_count > 0 && (
                <div className="text-xs bg-sky-50 border border-sky-100 rounded p-2 flex items-start gap-1.5 text-sky-800">
                  <Car className="h-3.5 w-3.5 mt-0.5" />
                  <span><b>{editing.rides_count}</b> ride{editing.rides_count !== 1 ? 's' : ''} assigned · worked {formatHours(editing.worked_hours)}</span>
                </div>
              )}
              {editing.status === 'working' && editing.rides_count > 0 && (!editing.shift_start || !editing.shift_end) && (
                <div className="text-xs bg-rose-50 border border-rose-200 rounded p-2 flex items-start gap-1.5 text-rose-800">
                  <AlertTriangle className="h-3.5 w-3.5 mt-0.5" />
                  <span>Shift start/end are required for working days. Please fill both.</span>
                </div>
              )}
              <div>
                <Label>Status</Label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  {Object.entries(STATUS_META).filter(([k]) => k !== 'unknown').map(([k, m]) => (
                    <Button
                      key={k}
                      type="button"
                      variant={editing.status === k ? 'default' : 'outline'}
                      className={editing.status === k ? `${m.color} font-semibold` : ''}
                      onClick={() => setEditing({ ...editing, status: k })}
                      data-testid={`attendance-status-${k}`}
                    >
                      <m.icon className="h-3.5 w-3.5 mr-1" /> {m.label}
                    </Button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Shift Start</Label>
                  <Input type="time" value={editing.shift_start || ''} onChange={(e) => setEditing({ ...editing, shift_start: e.target.value })} data-testid="attendance-shift-start" />
                </div>
                <div>
                  <Label>Shift End</Label>
                  <Input type="time" value={editing.shift_end || ''} onChange={(e) => setEditing({ ...editing, shift_end: e.target.value })} data-testid="attendance-shift-end" />
                </div>
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea rows={2} value={editing.notes || ''} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} data-testid="attendance-notes" />
              </div>
            </div>
          )}
          <DialogFooter className="!justify-between">
            <Button variant="outline" onClick={() => setEditorOpen(false)} data-testid="attendance-cancel"><X className="h-4 w-4 mr-1" /> Cancel</Button>
            <div className="flex items-center gap-2">
              {editing && editing.status === 'working' && editing.shift_start && editing.shift_end && !editing.shift_open && (
                <Button
                  onClick={reopenShift}
                  disabled={saving}
                  variant="outline"
                  className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                  data-testid="attendance-reopen-shift"
                  title="Driver pressed End Shift by mistake — reopen it so they can mark rides again."
                >
                  {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-1" />} Reopen Shift
                </Button>
              )}
              <Button onClick={handleSaveClick} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="attendance-save">
                {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />} Save
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Warning: driver already has rides on this day */}
      <Dialog open={warnRidesOpen} onOpenChange={setWarnRidesOpen}>
        <DialogContent className="max-w-md" data-testid="attendance-warn-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <AlertTriangle className="h-5 w-5" /> Driver has assigned rides
            </DialogTitle>
            <DialogDescription>
              This driver already has <b>{pendingChange?.rides_count}</b> ride{pendingChange?.rides_count !== 1 ? 's' : ''} assigned on this day. Do you want to continue and set the status to {STATUS_META[pendingChange?.status]?.label}?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="!justify-between">
            <Button variant="outline" onClick={() => setWarnRidesOpen(false)} data-testid="attendance-warn-cancel">Cancel</Button>
            <Button className="bg-amber-600 hover:bg-amber-700 text-white" onClick={() => performSave(pendingChange)} data-testid="attendance-warn-continue">
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SummaryCard({ label, value, icon: Icon, accent }) {
  const colors = {
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-800',
    slate:   'border-slate-300 bg-slate-50 text-slate-800',
    amber:   'border-amber-200 bg-amber-50 text-amber-800',
    sky:     'border-sky-200 bg-sky-50 text-sky-800',
    indigo:  'border-indigo-200 bg-indigo-50 text-indigo-800',
    rose:    'border-rose-200 bg-rose-50 text-rose-800',
  }[accent] || 'border-slate-200 bg-white text-slate-800';
  return (
    <div className={`rounded-md border p-3 ${colors}`}>
      <div className="flex items-center gap-1.5 text-xs font-medium opacity-80">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <div className="text-xl font-bold mt-0.5">{value}</div>
    </div>
  );
}
